/**
 * GamificationProgressPanel
 * 
 * Reusable panel showing user's gamification progress.
 * Used in both the header popup widget and the User Profile page.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useGamification } from './GamificationContext';
import { getAchievementsFromFirestore } from '../../utils/gamificationUtils';

/**
 * Stats configuration - single source of truth for all stats displays
 */
const STATS_CONFIG = [
  { key: 'dealsWon', label: 'Deals Won', icon: '💰', color: 'green', format: (v) => v },
  { key: 'revenueClosed', label: 'Revenue Closed', icon: '💎', color: 'emerald', format: (v) => `$${v.toLocaleString()}` },
  { key: 'tasksCompleted', label: 'Tasks Completed', icon: '✅', color: 'blue', format: (v) => v },
  { key: 'tasksOnTime', label: 'On-Time Tasks', icon: '⏰', color: 'cyan', format: (v) => v },
  { key: 'notesCreated', label: 'Notes Created', icon: '📝', color: 'purple', format: (v) => v },
  { key: 'customersUpdated', label: 'Customer Info Updated', icon: '🤝', color: 'teal', format: (v) => v },
  { key: 'contactsUpdated', label: 'Contact Info Updated', icon: '📞', color: 'indigo', format: (v) => v },
  { key: 'feedbackSubmitted', label: 'Feedback Given', icon: '💬', color: 'amber', format: (v) => v },
];

/**
 * Stats row component (for list view)
 */
const StatRow = ({ label, value, icon }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
    <div className="flex items-center gap-2">
      <span className="text-lg">{icon}</span>
      <span className="text-gray-600">{label}</span>
    </div>
    <span className="font-semibold text-gray-800">{value}</span>
  </div>
);

/**
 * Stats card component (for grid view)
 */
const StatCard = ({ icon, label, value, color = 'gray' }) => {
  const colorClasses = {
    green: 'bg-green-50 text-green-700 border-green-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  
  return (
    <div className={`p-4 rounded-xl border ${colorClasses[color] || colorClasses.gray}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl">{icon}</span>
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
};

/**
 * Reusable stats grid - used in LeaderboardPage and GamificationProgressPanel
 * @param {object} stats - The stats object from user profile
 * @param {string} variant - 'grid' for card layout, 'list' for row layout
 */
export const GamificationStatsGrid = ({ stats, variant = 'grid' }) => {
  if (variant === 'list') {
    return (
      <div className="space-y-3">
        {STATS_CONFIG.map(({ key, label, icon, format }) => (
          <StatRow 
            key={key}
            label={label} 
            value={format(stats?.[key] || 0)} 
            icon={icon} 
          />
        ))}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {STATS_CONFIG.map(({ key, label, icon, color, format }) => (
        <StatCard 
          key={key}
          icon={icon} 
          label={label} 
          value={format(stats?.[key] || 0)} 
          color={color}
        />
      ))}
    </div>
  );
};

/**
 * Main progress panel - used in popup and profile page
 */
export const GamificationProgressPanel = ({ 
  variant = 'popup', // 'popup' | 'page' - controls sizing/layout
  showHeader = true,
  onClose
}) => {
  const { profile, currentLevel, nextLevelInfo, ACHIEVEMENTS } = useGamification();
  const [activeTab, setActiveTab] = useState('overview');
  const [allAchievements, setAllAchievements] = useState([]);
  
  // Load all achievements from Firestore
  useEffect(() => {
    const loadAchievements = async () => {
      try {
        const data = await getAchievementsFromFirestore();
        setAllAchievements(data.filter(a => a.isEnabled !== false));
      } catch (error) {
        console.error('Failed to load achievements:', error);
        // Fall back to hard-coded achievements
        setAllAchievements(Object.values(ACHIEVEMENTS));
      }
    };
    loadAchievements();
  }, [ACHIEVEMENTS]);
  
  // Merge hard-coded and Firestore achievements
  const achievementsMap = useMemo(() => {
    const map = new Map();
    // Add hard-coded first
    Object.values(ACHIEVEMENTS).forEach(a => map.set(a.id, a));
    // Override with Firestore data
    allAchievements.forEach(a => map.set(a.id, a));
    return map;
  }, [ACHIEVEMENTS, allAchievements]);
  
  const earnedAchievements = useMemo(() => {
    return Array.from(achievementsMap.values()).filter(
      a => profile?.achievements?.includes(a.id)
    );
  }, [achievementsMap, profile]);
  
  const unearnedAchievements = useMemo(() => {
    return Array.from(achievementsMap.values()).filter(
      a => !profile?.achievements?.includes(a.id)
    );
  }, [achievementsMap, profile]);
  
  // Group earned achievements by category
  const achievementsByCategory = useMemo(() => {
    const categories = {};
    earnedAchievements.forEach(a => {
      const cat = a.category || 'other';
      if (!categories[cat]) categories[cat] = [];
      categories[cat].push(a);
    });
    return categories;
  }, [earnedAchievements]);
  
  const categoryLabels = {
    sales: '💰 Sales',
    tasks: '✅ Tasks', 
    engagement: '🤝 Engagement',
    consistency: '🔥 Consistency',
    quality: '💬 Quality',
    custom: '✨ Custom',
    other: '📦 Other'
  };
  
  if (!profile) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Loading progress...</div>
      </div>
    );
  }
  
  const isPage = variant === 'page';
  const containerClasses = isPage 
    ? 'bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden'
    : 'bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden';
  
  const contentMaxHeight = isPage ? 'max-h-[600px]' : 'max-h-80';
  
  return (
    <div className={containerClasses}>
      {/* Header */}
      {showHeader && (
        <div className="bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400 p-4 text-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-lg">Your {new Date().getFullYear()} Progress</h3>
            {onClose && (
              <button 
                onClick={onClose}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                ✕
              </button>
            )}
          </div>
          
          {/* Level Display */}
          <div className="flex items-center gap-4">
            <div className="text-5xl animate-glow rounded-full bg-white/20 p-3">
              {currentLevel?.icon}
            </div>
            <div>
              <div className="text-sm opacity-90">Level {currentLevel?.level}</div>
              <div className="text-xl font-bold">{currentLevel?.name}</div>
              <div className="text-2xl font-bold">
                {profile.totalPoints?.toLocaleString()} pts
              </div>
            </div>
          </div>
          
          {/* Progress to next level */}
          {nextLevelInfo?.nextLevel && (
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1 opacity-90">
                <span>Next: {nextLevelInfo.nextLevel.name}</span>
                <span>{nextLevelInfo.pointsNeeded.toLocaleString()} pts to go</span>
              </div>
              <div className="h-2 bg-white/30 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all"
                  style={{ width: `${nextLevelInfo.progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {['overview', 'achievements', 'stats'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
              activeTab === tab 
                ? 'text-amber-600 border-b-2 border-amber-500 bg-amber-50/50' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className={`p-4 ${contentMaxHeight} overflow-y-auto`}>
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Streak */}
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🔥</span>
                <div>
                  <div className="font-semibold text-gray-800">Current Streak</div>
                  <div className="text-sm text-gray-600">Keep it going!</div>
                </div>
              </div>
              <div className="text-2xl font-bold text-orange-600">
                {profile.currentStreak || 0} days
              </div>
            </div>
            
            {/* Best Streak */}
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-2xl">⭐</span>
                <div>
                  <div className="font-semibold text-gray-800">Best Streak</div>
                  <div className="text-sm text-gray-600">Your record</div>
                </div>
              </div>
              <div className="text-2xl font-bold text-purple-600">
                {profile.longestStreak || 0} days
              </div>
            </div>
            
            {/* Recent Achievements */}
            {earnedAchievements.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Recent Achievements</h4>
                <div className="flex flex-wrap gap-2">
                  {earnedAchievements.slice(0, isPage ? 8 : 6).map((achievement) => (
                    <div 
                      key={achievement.id}
                      className="p-2 bg-amber-50 rounded-lg text-center border border-amber-200"
                      title={achievement.description}
                    >
                      <div className="text-2xl">{achievement.icon}</div>
                      <div className="text-xs text-gray-600 mt-1">{achievement.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'achievements' && (
          <div className="space-y-6">
            {/* Earned by Category */}
            {earnedAchievements.length > 0 && (
              <div className="space-y-4">
                {Object.entries(achievementsByCategory).map(([category, achievements]) => (
                  <div key={category}>
                    <h4 className="text-sm font-semibold text-gray-600 mb-2">
                      {categoryLabels[category] || category}
                    </h4>
                    <div className={`grid gap-2 ${isPage ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5' : 'grid-cols-3'}`}>
                      {achievements.map((achievement) => (
                        <div 
                          key={achievement.id}
                          className="p-3 bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-lg text-center"
                          title={achievement.description}
                        >
                          <div className="text-3xl mb-1">{achievement.icon}</div>
                          <div className="text-xs font-medium text-gray-700 truncate">{achievement.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {earnedAchievements.length === 0 && (
              <div className="text-center py-6">
                <div className="text-4xl mb-2">🎯</div>
                <div className="text-gray-600">No achievements earned yet</div>
                <div className="text-sm text-gray-500 mt-1">
                  Complete tasks, close deals, and stay consistent to earn badges!
                </div>
              </div>
            )}
            
            {/* Locked */}
            {unearnedAchievements.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">
                  Locked ({unearnedAchievements.length})
                </h4>
                <div className={`grid gap-2 ${isPage ? 'grid-cols-4 sm:grid-cols-5 md:grid-cols-6' : 'grid-cols-3'}`}>
                  {unearnedAchievements.map((achievement) => (
                    <div 
                      key={achievement.id}
                      className="p-3 bg-gray-100 rounded-lg text-center opacity-60"
                      title={`${achievement.name}: ${achievement.description}`}
                    >
                      <div className="text-3xl mb-1 grayscale">{achievement.icon}</div>
                      <div className="text-xs text-gray-500 truncate">{achievement.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Summary */}
            <div className="pt-4 border-t flex items-center justify-between text-sm">
              <span className="text-gray-600">
                {earnedAchievements.length} of {achievementsMap.size} achievements earned
              </span>
              <span className="text-amber-600 font-medium">
                +{earnedAchievements.reduce((sum, a) => sum + (a.points || 0), 0).toLocaleString()} bonus pts
              </span>
            </div>
          </div>
        )}
        
        {activeTab === 'stats' && (
          <GamificationStatsGrid stats={profile.stats} variant="list" />
        )}
      </div>
    </div>
  );
};

export default GamificationProgressPanel;
